/**
 * Client-side video duration checks for posting to X (KGH-785).
 *
 * X rejects over-length videos only AFTER the upload, and the failure surfaces
 * as a generic error — every one of our ">27 MB" X failures was a clip over
 * 2:20. Read the duration from the file up front and say so.
 */

// X's cap: 2:20 for standard accounts. Premium can go much longer; we cap it
// at 10 minutes because beyond that our own pipeline is unproven.
export const X_MAX_VIDEO_SECONDS = 140;
export const X_PREMIUM_MAX_VIDEO_SECONDS = 600;

/**
 * Read a video File's duration in seconds via a detached <video> element.
 * Resolves null when the metadata can't be read (unsupported container, etc.)
 * so callers never block on it — the server and X still validate.
 */
export function getVideoDurationSeconds(file: File, timeoutMs = 8000): Promise<number | null> {
  return new Promise((resolve) => {
    let url: string;
    try {
      url = URL.createObjectURL(file);
    } catch (e) {
      resolve(null);
      return;
    }
    const video = document.createElement('video');
    let done = false;

    const finish = (value: number | null) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      video.removeAttribute('src');
      try { video.load(); } catch (e) { /* ignore */ }
      URL.revokeObjectURL(url);
      resolve(value);
    };

    const timer = window.setTimeout(() => finish(null), timeoutMs);
    video.preload = 'metadata';
    video.onloadedmetadata = () => finish(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => finish(null);
    video.src = url;
  });
}

export function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  const remainder = total % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

/** User copy when a video exceeds X's cap for this account, otherwise null. */
export function xVideoDurationError(durationSeconds: number, isPremium: boolean): string | null {
  const limit = isPremium ? X_PREMIUM_MAX_VIDEO_SECONDS : X_MAX_VIDEO_SECONDS;
  if (durationSeconds <= limit) return null;
  const premiumNote = isPremium ? '' : ' (longer with X Premium)';
  return `This video is ${formatDuration(durationSeconds)} long. X allows videos up to ${formatDuration(limit)}${premiumNote}. Please trim it and try again.`;
}
